import React from "react";
import { motion } from "framer-motion";
import { Box, Typography, Button, Paper } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";

export type ProductListToolbarProps = {
  title?: string;
  onOpenFilters?: (event: React.MouseEvent<HTMLElement>) => void;
  onAddProduct: () => void;
  addProductDisabled?: boolean;
  children?: React.ReactNode;
  isMobile?: boolean;
};

const ProductListToolbar: React.FC<ProductListToolbarProps> = ({
  title,
  onOpenFilters,
  onAddProduct,
  addProductDisabled = false,
  children,
  isMobile = false
}) => {
  const { t } = useKivunovaTranslation();

  return (
    <Paper
      component={motion.div}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper"
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexDirection={isMobile ? "column" : "row"}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: 1.5,
              backgroundColor: `${BRAND_COLOR}14`
            }}
          >
            <Inventory2OutlinedIcon sx={{ color: BRAND_COLOR, fontSize: 22 }} />
          </Box>
          <Typography variant="h5" fontWeight="700" color="text.primary">
            {title ?? t("products-list", "Products List")}
          </Typography>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
          flexDirection={isMobile ? "column" : "row"}
          flexWrap="wrap"
        >
          <Button
            variant="outlined"
            size="medium"
            startIcon={<FilterListIcon />}
            onClick={onOpenFilters}
            aria-haspopup="true"
            sx={{
              borderColor: "divider",
              color: "text.secondary",
              textTransform: "none",
              fontWeight: 500,
              "&:hover": {
                borderColor: BRAND_COLOR,
                color: BRAND_COLOR,
                backgroundColor: `${BRAND_COLOR}08`
              }
            }}
          >
            {t("filters", "Filters")}
          </Button>
          <Button
            variant="contained"
            size="medium"
            onClick={onAddProduct}
            disabled={addProductDisabled}
            data-testid="add-product-btn"
            sx={{
              backgroundColor: BRAND_COLOR,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: `0 2px 8px ${BRAND_COLOR}40`,
              "&:hover": {
                backgroundColor: BRAND_HOVER,
                boxShadow: `0 4px 12px ${BRAND_COLOR}50`
              }
            }}
          >
            {t("add-new-product", "Add New Product")}
          </Button>
          {children}
        </Box>
      </Box>
    </Paper>
  );
};

export default ProductListToolbar;
